/**
 * SmartdineNow API – Optimized Manual Wrapper
 * Feature-compatible with Swagger generated service
 */

import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';

import { RestaurantBranchTableEntity } from '../model/restaurantBranchTableEntity';
import { SmartDineResponseRestaurantBranchTableEntity } from '../model/smartDineResponseRestaurantBranchTableEntity';

import { BASE_PATH } from '../variables';
import { Configuration } from '../configuration';

@Injectable({
    providedIn: 'root'
})
export class RestaurantBranchTableService {

    protected basePath = 'http://localhost';
    private defaultHeaders = new HttpHeaders();

    constructor(
        private httpClient: HttpClient,
        @Optional() @Inject(BASE_PATH) basePath: string,
        @Optional() private configuration: Configuration
    ) {
        if (basePath) {
            this.basePath = basePath;
        }
        if (!this.configuration) {
            this.configuration = new Configuration();
        }
    }

    /* ------------------------------------------------------------------ */
    /* ---------------------- COMMON UTILITIES --------------------------- */
    /* ------------------------------------------------------------------ */

    private buildHeaders(consumes?: string[]): HttpHeaders {
        let headers = this.defaultHeaders;

        if (this.configuration.accessToken) {
            const token = typeof this.configuration.accessToken === 'function'
                ? this.configuration.accessToken()
                : this.configuration.accessToken;
            headers = headers.set('Authorization', 'Bearer ' + token);
        }

        headers = headers.set('Accept', '*/*');

        if (consumes && consumes.length) {
            const contentType = this.configuration.selectHeaderContentType(consumes);
            if (contentType) {
                headers = headers.set('Content-Type', contentType);
            }
        }

        return headers;
    }

    private withCreds() {
        return this.configuration.withCredentials;
    }

    /* ------------------------------------------------------------------ */
    /* ------------------------ CRUD METHODS ----------------------------- */
    /* ------------------------------------------------------------------ */

    /** CREATE TABLE */
    public createRestaurantBranchTable(body: RestaurantBranchTableEntity, restaurantId: string, branchId: string, observe: any = 'body', reportProgress = false): Observable<any> {

        if (!body || !restaurantId || !branchId) {
            throw new Error('Required parameter was null or undefined.');
        }

        return this.httpClient.request(
            'post',
            `${this.basePath}/api/admin/restaurants/${encodeURIComponent(restaurantId)}/branches/${encodeURIComponent(branchId)}/tables`,
            {
                body,
                headers: this.buildHeaders(['application/json']),
                withCredentials: this.withCreds(),
                observe,
                reportProgress
            }
        );
    }

    /** UPDATE TABLE (PUT – FULL ENTITY) */
    public updateRestaurantBranchTable(body: RestaurantBranchTableEntity, restaurantId: string, branchId: string, tableId: string, observe: any = 'body', reportProgress = false): Observable<any> {

        if (!body || !restaurantId || !branchId || !tableId) {
            throw new Error('Required parameter was null or undefined.');
        }

        return this.httpClient.request(
            'put',
            `${this.basePath}/api/admin/restaurants/${encodeURIComponent(restaurantId)}/branches/${encodeURIComponent(branchId)}/tables/${encodeURIComponent(tableId)}`,
            {
                body,
                headers: this.buildHeaders(['application/json']),
                withCredentials: this.withCreds(),
                observe,
                reportProgress
            }
        );
    }

    /** DELETE TABLE */
    public deleteRestaurantBranchTable(restaurantId: string, branchId: string, tableId: string, observe: any = 'body', reportProgress = false): Observable<any> {

        if (!restaurantId || !branchId || !tableId) {
            throw new Error('Required parameter was null or undefined.');
        }

        return this.httpClient.request(
            'delete',
            `${this.basePath}/api/admin/restaurants/${encodeURIComponent(restaurantId)}/branches/${encodeURIComponent(branchId)}/tables/${encodeURIComponent(tableId)}`,
            {
                headers: this.buildHeaders(),
                withCredentials: this.withCreds(),
                observe,
                reportProgress
            }
        );
    }

    /** GET SINGLE TABLE */
    public getRestaurantBranchTable(restaurantId: string, branchId: string, tableId: string, observe: any = 'body', reportProgress = false): Observable<any> {

        if (!restaurantId || !branchId || !tableId) {
            throw new Error('Required parameter was null or undefined.');
        }

        return this.httpClient.request(
            'get',
            `${this.basePath}/api/admin/restaurants/${encodeURIComponent(restaurantId)}/branches/${encodeURIComponent(branchId)}/tables/${encodeURIComponent(tableId)}`,
            {
                headers: this.buildHeaders(),
                withCredentials: this.withCreds(),
                observe,
                reportProgress
            }
        );
    }

    /** GET ALL TABLES IN BRANCH */
    public getRestaurantBranchTables(restaurantId: string,branchId: string,observe: any = 'body',reportProgress = false): Observable<any> {

        if (!restaurantId || !branchId) {
            throw new Error('Required parameter was null or undefined.');
        }

        return this.httpClient.request(
            'get',
            `${this.basePath}/api/admin/restaurants/${encodeURIComponent(restaurantId)}/branches/${encodeURIComponent(branchId)}/tables`,
            {
                headers: this.buildHeaders(),
                withCredentials: this.withCreds(),
                observe,
                reportProgress
            }
        );
    }

    /* ------------------------------------------------------------------ */
    /* -------------------- STATUS UPDATE METHODS ------------------------ */
    /* ------------------------------------------------------------------ */

    /** PATCH – SET SINGLE TABLE STATUS = AVAILABLE */
    updateTableStatusToAvailable(
        restaurantId: string,
        branchId: string,
        tableId: string,
        observe: any = 'body',
        reportProgress = false
    ): Observable<any> {

        if (!restaurantId || !branchId || !tableId) {
            throw new Error('Required parameter was null or undefined.');
        }

        return this.httpClient.request(
            'patch',
            `${this.basePath}/api/admin/restaurants/${encodeURIComponent(restaurantId)}/branches/${encodeURIComponent(branchId)}/tables/${encodeURIComponent(tableId)}`,
            {
                headers: this.buildHeaders(),
                withCredentials: this.withCreds(),
                observe,
                reportProgress
            }
        );
    }

    /** PATCH – SET ALL TABLES IN BRANCH = AVAILABLE */
    updateAllTablesStatusToAvailable(
        restaurantId: string,
        branchId: string,
        observe: any = 'body',
        reportProgress = false
    ): Observable<any> {

        if (!restaurantId || !branchId) {
            throw new Error('Required parameter was null or undefined.');
        }

        return this.httpClient.request(
            'patch',
            `${this.basePath}/api/admin/restaurants/${encodeURIComponent(restaurantId)}/branches/${encodeURIComponent(branchId)}`,
            {
                headers: this.buildHeaders(),
                withCredentials: this.withCreds(),
                observe,
                reportProgress
            }
        );
    }
}
